import { useEffect, useState } from "react";
import { getDistrictProfile, getMeta, getReallocation } from "../lib/api";
import { EvidencePanel } from "../components/EvidencePanel";
import { ResultNumbers } from "../components/ResultNumbers";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { Drawer, EvidenceButton } from "../components/Drawer";
import { CaveatNote, StatTile, rateTone } from "../components/Evidence";
import { DistrictProfile } from "../components/Briefs";
import { Select } from "./PartnersPage";
import { inr, num, pct } from "../lib/format";

const DIMENSIONS = [
  { value: "district", label: "By district" },
  { value: "state", label: "By state" },
  { value: "theme", label: "By theme" },
  { value: "partner", label: "By partner" },
];

export default function ReallocationPage() {
  const [meta, setMeta] = useState(null);
  const [dimension, setDimension] = useState("district");
  const [fy, setFy] = useState("");
  const [theme, setTheme] = useState("");
  const [data, setData] = useState(null);
  const [evidence, setEvidence] = useState(false);
  const [district, setDistrict] = useState(null);
  const [districtErr, setDistrictErr] = useState(null);
  const [err, setErr] = useState(null);

  const openDistrict = (row) => {
    setDistrict({ loading: true, label: row.cell });
    setDistrictErr(null);
    getDistrictProfile(row.cell_state, row.cell_district)
      .then((d) => setDistrict(d))
      .catch((e) => setDistrictErr(e));
  };

  useEffect(() => {
    getMeta().then(setMeta).catch(setErr);
  }, []);

  useEffect(() => {
    setData(null);
    getReallocation({ dimension, fy: fy || undefined, theme: theme || undefined })
      .then(setData)
      .catch(setErr);
  }, [dimension, fy, theme]);

  return (
    <div>
      <PageHeader title="Where spend is concentrated versus where it is utilised"
                  testId="reallocation-header"
                  actions={data && <EvidenceButton onClick={() => setEvidence(true)}
                                                   answerability={data.answerability} />} />

      <div className="flex gap-2 flex-wrap mb-6" data-testid="reallocation-filters">
        <Select value={dimension} onChange={setDimension} options={DIMENSIONS} testId="filter-dimension" />
        <Select value={fy} onChange={setFy} label="All financial years" testId="filter-fy"
                options={(meta?.fys || []).map((f) => ({ value: f, label: f }))} />
        <Select value={theme} onChange={setTheme} label="All themes" testId="filter-theme"
                options={(meta?.themes || []).map((t) => ({ value: t.key, label: t.label }))} />
      </div>

      {err && <ErrorBox error={err} />}
      {!data && !err && <Loading />}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 mb-8"
               data-testid="reallocation-summary">
            <StatTile label="Total committed" tone="brand" value={inr(data.total_committed)} />
            <StatTile label="Portfolio utilisation" tone={rateTone(data.portfolio_utilisation_rate_pct)}
                      value={pct(data.portfolio_utilisation_rate_pct)}
                      sub="grants whose period has ended" />
            <StatTile label={`Top-5 ${data.dimension} share`} tone="amber"
                      value={pct(data.top5_share_pct)} />
            <StatTile label="Concentration (HHI)" tone="sky"
                      value={Number(data.hhi).toFixed(4)} sub="0 = spread, 1 = one cell" />
            <StatTile label="Mean ₹ per beneficiary" tone="neutral"
                      value={inr(data.portfolio_mean_cost_per_beneficiary)}
                      sub="mGrant-internal only" />
          </div>
          <ResultNumbers result={data} onDistrictClick={openDistrict} />
        </>
      )}

      <Drawer open={!!district} onClose={() => setDistrict(null)} testId="district-drawer">
        {districtErr && <ErrorBox error={districtErr} />}
        {district?.loading && <Loading />}
        {district && !district.loading && <DistrictProfile profile={district} />}
      </Drawer>

      <Drawer open={evidence} onClose={() => setEvidence(false)} testId="reallocation-evidence-drawer">
        {data && (
          <EvidencePanel result={data} testId="reallocation-evidence">
            <div className="space-y-2.5">
              <CaveatNote title="How this is calculated">
                A cell must sit in the top decile of spend share before it can be flagged —
                concentration is the precondition, not the reason. Utilisation and stalled capital
                are measured only on grants whose period has ended; money still in flight is shown
                separately and never triggers a flag.
              </CaveatNote>
              <Row label="Grants in slice" value={num(data.grant_count)} />
              <Row label={`${data.dimension} cells`} value={num(data.cell_count)} />
              <Row label="Total committed" value={inr(data.total_committed)} />
              <Row label="HHI (0–1)" value={Number(data.hhi).toFixed(4)} />
              <Row label="Top-5 share" value={pct(data.top5_share_pct)} />
              <Row label="Portfolio utilisation" value={pct(data.portfolio_utilisation_rate_pct)} />
              <Row label="Flag threshold (p90 share)" value={pct(data.spend_share_threshold_pct, 2)} />
              <p className="text-[13px] text-stone-600 leading-relaxed pt-2">
                {data.threshold_rule}
              </p>
              {data.aspirational_districts && (
                <p className="text-[13px] text-stone-600 leading-relaxed">
                  {data.aspirational_districts.source}. A district is labelled on the list, not on
                  the list, or — for West Bengal — not in the published list.{" "}
                  {data.aspirational_districts.caveat}
                </p>
              )}
            </div>
          </EvidencePanel>
        )}
      </Drawer>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-stone-100 pb-2">
    <span className="text-[13px] text-stone-600">{label}</span>
    <span className="num text-[13px] text-stone-900">{String(value ?? "—")}</span>
  </div>
);
